import { Controller } from "@hotwired/stimulus"

// The signature — "The Living Structure" (CLAUDE_DESIGN §2.2).
// The Root-and-Canopy SVG is server-rendered fully-formed; this controller
// animates stroke-dashoffset on scroll so it draws itself roots → trunk →
// canopy, and cross-fades the stage captions. Reduced-motion → drawn, static.
//
// Each [data-draw] path carries data-s / data-e (the scroll-progress window in
// which it draws); each [data-node] carries data-appear (progress at which it
// fades in). Progress is the tree section's travel through a sticky viewport.
export default class extends Controller {
  connect() {
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.paths = Array.from(this.element.querySelectorAll("[data-draw]"))
    this.nodes = Array.from(this.element.querySelectorAll("[data-node]"))
    this.stages = {
      roots: this.element.querySelector('[data-stage="roots"]'),
      trunk: this.element.querySelector('[data-stage="trunk"]'),
      canopy: this.element.querySelector('[data-stage="canopy"]'),
    }

    this.paths.forEach((p) => {
      const len = p.getTotalLength()
      p.style.strokeDasharray = len
      p.dataset.len = len
      p.style.strokeDashoffset = this.reduced ? 0 : len
    })
    this.nodes.forEach((n) => {
      n.style.transition = "opacity 300ms var(--ease)"
      n.style.opacity = this.reduced ? 1 : 0
    })

    this.setStage(this.reduced ? 1 : 0)
    if (this.reduced) return

    this.ticking = false
    this.onScroll = this.onScroll.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onScroll)

    this.draw()
    this.t1 = setTimeout(() => this.draw(), 180)
    this.t2 = setTimeout(() => this.draw(), 640)
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("resize", this.onScroll)
    clearTimeout(this.t1)
    clearTimeout(this.t2)
  }

  onScroll() {
    if (this.ticking) return
    this.ticking = true
    requestAnimationFrame(() => { this.ticking = false; this.draw() })
  }

  progress() {
    const rect = this.element.getBoundingClientRect()
    const vh = window.innerHeight
    const total = rect.height - vh
    let p = total > 0 ? -rect.top / total : rect.top <= 0 ? 1 : 0
    return Math.max(0, Math.min(1, p))
  }

  draw() {
    const p = this.progress()
    this.paths.forEach((el) => {
      const s = +el.dataset.s
      const e = +el.dataset.e
      const len = +el.dataset.len
      const local = Math.max(0, Math.min(1, (p - s) / (e - s)))
      el.style.strokeDashoffset = len * (1 - local)
    })
    this.nodes.forEach((el) => {
      const a = +el.dataset.appear
      el.style.opacity = Math.max(0, Math.min(1, (p - a) / 0.05))
    })
    this.setStage(p)
  }

  setStage(p) {
    if (!this.stages.roots) return
    const active = p < 0.46 ? "roots" : p < 0.6 ? "trunk" : "canopy"
    Object.keys(this.stages).forEach((k) => {
      if (this.stages[k]) this.stages[k].style.opacity = k === active ? 1 : 0.28
    })
  }
}
